import MenuItem from '@mui/material/MenuItem'
import Slider from '@mui/material/Slider'
import Tab from '@mui/material/Tab'
import React, {FC, useState} from 'react'
import {useDispatch} from 'react-redux'
import {
  ageList,
  continentsList,
  findMeItems,
  FindMeKeys,
  maxAge,
  minAge,
  setFilter,
  tabList,
  useAllSearchFilters
} from 'entities/searchPeople'
import {useHideForScroll} from 'shared/hooks/useHideForScroll'
import {getValueFromArray, getValueFromObject} from 'shared/lib'
import {Button} from 'shared/ui/buttons'
import {ArrowDown, ArrowLeft, Search} from 'shared/ui/icons'
import {Modal} from 'shared/ui/modal'
import {Select} from 'shared/ui/select'
import {Tabs} from 'shared/ui/tabs'
import styles from './styles.module.scss'

export const SearchPeopleMobile: FC<{wrapperRef: React.RefObject<HTMLDivElement>}> = ({wrapperRef}) => {
  const dispatch = useDispatch()
  const {tab, findMe, ageFrom, ageTo, continents} = useAllSearchFilters()
  const [isOpenFilters, setIsOpenFilters] = useState(false)
  const isShowForScroll = useHideForScroll(wrapperRef, 150)

  return (
    <>
      <div className={`${styles.wrapper} ${!isShowForScroll ? styles.hide : ''}`}>
        <div className={styles.search} onClick={() => setIsOpenFilters(true)}>
          <div className={styles.searchIcon} >
            <Search />
          </div>
          <span>
            {
              continents
                ? getValueFromArray(continentsList, continents).label + ', '
                : 'Find me ' + (findMe !== FindMeKeys.anyone ? 'a ' : '')
            }
            {getValueFromObject(findMeItems, findMe).label}
            , {ageFrom || minAge}-{ageTo || (maxAge + '+')}
          </span>
          <div className={styles.arrowDown}>
            <ArrowDown />
          </div>
        </div>
        <Tabs
          value={tab}
          onChange={(_, newValue) => dispatch(setFilter({value: newValue, key: 'tab'}))}
          className={styles.tabs}
          classes={{flexContainer: styles.tabsFlexContainer}}
        >
          {tabList.map((tab) => <Tab value={tab.id} key={tab.id} label={tab.label} className={styles.tab} />)}
        </Tabs>
      </div>
      <FiltersModal isOpen={isOpenFilters} onClose={() => setIsOpenFilters(false)} />
    </>
  )
}

type FiltersModalProps = {
  isOpen: boolean
  onClose: () => void
}

type ChangeFieldsType = {
  continents: string,
  ageTo: string,
  ageFrom: string,
  findMe: FindMeKeys
}

export const FiltersModal: FC<FiltersModalProps> = ({isOpen, onClose}) => {
  const dispatch = useDispatch()
  const {continents, ageTo, ageFrom, findMe} = useAllSearchFilters()
  const [fields, setFields] = useState<ChangeFieldsType>({
    continents,
    ageFrom: ageFrom || String(minAge),
    ageTo: ageTo || String(maxAge),
    findMe
  })

  const handleChange = (_: Event, newValue: number | number[]) => {
    if (!Array.isArray(newValue)) return
    setFields((last) => ({...last, ageFrom: ageList[newValue[0]], ageTo: ageList[newValue[1]]}))
  }

  const applyHandler = () => {
    dispatch(setFilter({value: fields.continents, key: 'continents'}))
    dispatch(setFilter({value: fields.ageFrom, key: 'ageFrom'}))
    dispatch(setFilter({value: fields.ageTo, key: 'ageTo'}))
    dispatch(setFilter({value: fields.findMe, key: 'findMe'}))
    onClose()
  }

  const onCloseHandler = () => {
    setFields({
      continents,
      ageFrom: ageFrom || String(minAge),
      ageTo: ageTo || String(maxAge),
      findMe
    })
    onClose()
  }

  if (!isOpen) return <></>

  return (
    <Modal
      open={isOpen}
      onClose={onCloseHandler}
    >
      <div className={styles.popup}>
        <div className={styles.header} onClick={onCloseHandler}>
          <div className={styles.arrowLeft}>
            <ArrowLeft />
          </div>
          People
        </div>

        <div className={styles.filters}>
          <label className={styles.label}>Continents:</label>
          <Select
            value={fields.continents}
            onChange={(event) => setFields((last) => ({...last, continents: event.target.value as string}))}
            displayEmpty
            placeholder='All continents'
          >
            {
              continentsList.map((item) => <MenuItem key={item.id} value={item.id}>{item.label}</MenuItem>)
            }
          </Select>
        </div>

        <div className={styles.filters}>
          <label className={styles.label}>Find me:</label>
          <div className={styles.findMeButtons}>
            <Button
              styleType='secondary'
              className={`${styles.button} ${fields.findMe === FindMeKeys.female ? styles.active : null}`}
              onClick={() => setFields((last) => ({...last, findMe: FindMeKeys.female}))}
            >
              {findMeItems.female.label}
            </Button>
            <Button
              styleType='secondary'
              className={`${styles.button} ${fields.findMe === FindMeKeys.male ? styles.active : null}`}
              onClick={() => setFields((last) => ({...last, findMe: FindMeKeys.male}))}
            >
              {findMeItems.male.label}
            </Button>
            <Button
              styleType='secondary'
              className={`${styles.button} ${fields.findMe === FindMeKeys.anyone ? styles.active : null}`}
              onClick={() => setFields((last) => ({...last, findMe: FindMeKeys.anyone}))}
            >
              {findMeItems.anyone.label}
            </Button>
          </div>
        </div>

        <div className={`${styles.filters} ${styles.age}`}>
          <label className={styles.label}>Age: {fields.ageFrom}-{fields.ageTo}</label>
          <Slider
            min={0}
            max={ageList.length - 1}
            value={[ageList.indexOf(fields.ageFrom), ageList.indexOf(fields.ageTo)]}
            onChange={handleChange}
            className={styles.sliderAge}
            classes={{
              root: styles.sliderRoot,
              track: styles.sliderTrack,
              thumb: styles.sliderThumb,
              active: styles.sliderActive,
              focusVisible: styles.sliderFocusVisible,
              rail: styles.sliderRail
            }}
          />
        </div>


        <Button styleType='primary' className={styles.apply} onClick={applyHandler}>Apply</Button>
      </div>
    </Modal>
  )
}
